// Credential types for @flashbacktech/tsclient.
//
// Schema mirrors cloud-agent-backend's Credential row (renamed from APIKey
// in migration 0010). Every provider — cloud (AWS/GCP/AZURE), infra
// (SSH), VCS (GitHub/GitLab/Azure DevOps), AI/LLM (OpenAI/Anthropic) —
// uses the same `(provider, label, secret, metadata)` shape.
// Provider-specific fields live in `metadata`; the catalog at
// ./providers.ts is the source of truth for what each provider's fields
// look like.
//
// Sandbox-scoped FOA bearer tokens (formerly RepoAiApiKey) are now
// modelled separately as GatewayToken — see below.

// ProviderID is open: the catalog defines a default set, but we allow
// any string so customer-extended catalogs are still typed.
export type ProviderID = 'AWS' | 'BEDROCK' | 'GCP' | 'AZURE' | 'OCI' | 'SSH' | 'GITHUB' | 'GITLAB' | 'AZURE_DEVOPS' | 'OPENAI' | 'ANTHROPIC' | 'GEMINI' | string;

// ── Universal credential ─────────────────────────────────────────────

// Credential is the read-projection of one row in the Credential table.
// Never includes the secret. `displayHint` is the catalog-computed
// snippet for listings (e.g. "AKIATEST… / us-east-1",
// "ubuntu@vps.example.com:22").
export interface Credential {
  id: string;
  provider: ProviderID;
  label: string;
  metadata: Record<string, unknown>;
  displayHint?: string;
  createdAt: string;
}

// CreateCredentialRequest is the universal create body. Provider-specific
// fields go inside `metadata`; the catalog validates the shape both
// client-side (./providers.ts:validate) and server-side.
export interface CreateCredentialRequest {
  provider: ProviderID;
  label: string;
  secret: string;
  metadata?: Record<string, unknown>;
  projectId: string;
}

// UpdateCredentialRequest is partial. Provider is immutable on update; to
// change provider, delete and re-create. `metadata` is replace-wholesale
// when present.
export interface UpdateCredentialRequest {
  label?: string;
  secret?: string;
  metadata?: Record<string, unknown>;
}

// ListCredentialsQuery narrows /credentials by project and optionally by
// provider id or capability bit (storage|ai|compute|vcs|notify).
export interface ListCredentialsQuery {
  projectId: string;
  provider?: ProviderID;
  capability?: 'storage' | 'ai' | 'compute' | 'vcs' | 'notify';
}

// SandboxCredential is one row in the SandboxCredential join table —
// links a sandbox to a project-level credential. The PK is
// (sandboxId, credentialId), so association identity is the pair itself
// (no synthetic association id).
export interface SandboxCredential {
  sandboxId: string;
  credentialId: string;
  provider: ProviderID;
  label: string;
  displayHint?: string;
  createdAt: string;
}

// ── Sandbox gateway tokens ───────────────────────────────────────────

// GatewayToken is a sandbox-scoped, OpenAI-compatible bearer used by the
// agent engine to authenticate to FlashOnAI. The plaintext secret is
// returned exactly once — at creation — and never re-fetchable.
export interface GatewayToken {
  id: string;
  sandboxId: string;
  label: string;
  createdAt: string;
}

export interface CreateGatewayTokenRequest {
  label: string;
}

// CreateGatewayTokenResponse is the create-time-only shape — `secret`
// holds the plaintext bearer the caller must surface ("copy now")
// before navigating away.
export interface CreateGatewayTokenResponse {
  token: GatewayToken;
  secret: string;
}

// AgentToken is a sandbox-scoped bearer an EXTERNAL agent (Claude Code,
// Cursor, Codex, …) presents to the MCP server (/mcp) to drive this
// sandbox's cloud agent. Only a hash is stored server-side; the plaintext
// (`secret`) is returned exactly once at creation. `tokenPrefix` is a
// non-secret display fragment ("fba_AbCd…") so a list can disambiguate
// rotated tokens.
export interface AgentToken {
  id: string;
  sandboxId: string;
  label: string;
  tokenPrefix: string;
  lastUsedAt?: string;
  createdAt: string;
}

export interface CreateAgentTokenRequest {
  label: string;
}

// CreateAgentTokenResponse is the create-time-only shape — `secret` holds
// the plaintext bearer the caller must surface ("copy now") before
// navigating away.
export interface CreateAgentTokenResponse {
  token: AgentToken;
  secret: string;
}

// ── Common envelope ──────────────────────────────────────────────────

export interface SimpleKeysResponse {
  success: boolean;
  message?: string;
  data?: unknown;
}

// ── Wire shapes (legacy field-name aliases collapse here) ────────────

export interface WireSandboxCredential {
  // Backend wire emits sandboxId; older clients may still see repoId.
  // Normaliser collapses both into SandboxCredential.sandboxId.
  sandboxId?: string;
  repoId?: string;
  credentialId: string;
  provider: string;
  label: string;
  displayHint?: string | null;
  createdAt: string;
}

export interface WireGatewayToken {
  id: string;
  // Backend wire emits sandboxId via "repoId" today (StorageRepo/Sandbox
  // alias); accept both.
  sandboxId?: string;
  repoId?: string;
  label: string;
  createdAt: string;
}

export function normalizeSandboxCredential(wire: WireSandboxCredential): SandboxCredential {
  return {
    sandboxId: wire.sandboxId ?? wire.repoId ?? '',
    credentialId: wire.credentialId,
    provider: wire.provider,
    label: wire.label,
    displayHint: wire.displayHint ?? undefined,
    createdAt: wire.createdAt,
  };
}

export function normalizeGatewayToken(wire: WireGatewayToken): GatewayToken {
  return {
    id: wire.id,
    sandboxId: wire.sandboxId ?? wire.repoId ?? '',
    label: wire.label,
    createdAt: wire.createdAt,
  };
}

export interface WireAgentToken {
  id: string;
  // Backend emits sandboxId; accept repoId too for forward/backward compat.
  sandboxId?: string;
  repoId?: string;
  label: string;
  tokenPrefix: string;
  lastUsedAt?: string | null;
  createdAt: string;
}

export function normalizeAgentToken(wire: WireAgentToken): AgentToken {
  return {
    id: wire.id,
    sandboxId: wire.sandboxId ?? wire.repoId ?? '',
    label: wire.label,
    tokenPrefix: wire.tokenPrefix,
    lastUsedAt: wire.lastUsedAt ?? undefined,
    createdAt: wire.createdAt,
  };
}
